import { notification } from "antd";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UPDATE_PRICER } from "utils/api/graphql/mutations/pricers";
import { INSURANCE_PLAN } from "utils/api/graphql/queries/insurance-plans";
import { PRICER, PRICERS } from "utils/api/graphql/queries/pricers";
import { INSURANCE_PLAN_RISKS, PRICER_TYPES, SCREENS } from "utils/constants";
import View from "../../RateLookup/View";
import { toPricerDatePayload, toRateLookupPrices } from "../../RateLookup/form";

const DetailRateLookup = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const ERRORS = t("ERRORS", { returnObjects: true });
  const navigate = useNavigate();
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);
  const { id } = useParams();

  const [updatePricer] = useMutation(UPDATE_PRICER, {
    refetchQueries: [
      {
        query: PRICERS,
        awaitRefetchQueries: true,
      },
    ],
    onCompleted: () => {
      notification.success({ message: t("DETAIL.NOTIFICATION_SUCCESS") });
      navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);
    },
    onError: (error) => {
      notification.error({ message: error.message || ERRORS.DATE_CONFLICT });
    },
  });

  const { data, loading, error } = useQuery(PRICER, {
    variables: { where: { id } },
  });
  const planId = get(data, "pricer.insurancePlan.id");
  const hasOwnRates = Boolean(get(data, "pricer.prices.rates"));
  const { data: planData, loading: planLoading } = useQuery(INSURANCE_PLAN, {
    variables: { where: { id: planId } },
    skip: !planId || hasOwnRates,
  });
  const coveragePricers = (
    get(planData, "insurancePlan.insurancePlanCoverages") || []
  )
    .map((coverage) => coverage.pricer)
    .filter((pricer) => get(pricer, "prices.rates"));

  const onSubmit = ({ rates, dates, ...values }) => {
    const existing = get(data, "pricer.prices") || {};
    const prices = existing.rates
      ? toRateLookupPrices(rates, existing)
      : existing;
    return updatePricer({
      variables: {
        where: { id },
        data: {
          ...values,
          type: get(data, "pricer.type") || PRICER_TYPES.COMPOSABLE,
          ...toPricerDatePayload(dates),
          prices,
        },
      },
    });
  };

  if (loading || error || planLoading) return <Loading />;

  return (
    <View
      onSubmit={onSubmit}
      data={omitDeep(data.pricer, "__typename")}
      cancel={cancel}
      coveragePricers={coveragePricers}
      onOpenCoverage={(pricer) =>
        navigate(`${SCREENS.PRIVATE.ADMIN.PRICERS.DETAIL.path}/${pricer.id}`, {
          state: { risk: INSURANCE_PLAN_RISKS.SPORTS_LEISURE },
        })
      }
    />
  );
};

export default DetailRateLookup;
